
import { Component, Prop, Vue } from "vue-property-decorator";
import { mapState } from "vuex";
import vModal from "@/components/vModal.vue";
import {
  GenerateReportCategoryOption,
  GenerateReportOption,
  ReportCategoryScopeMode,
} from "@/models/Reports/GenerateReport";
import { LocalizationState } from "@/store/modules/LocalizationModule";

@Component({
  components: {
    vModal,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
export default class vGenerateReportModal extends Vue {
  localizationState!: LocalizationState;
  @Prop({ default: false }) readonly show!: boolean;
  @Prop({ default: "" }) readonly reportContext!: string;
  @Prop({ default: "" }) readonly modalTitle!: string;
  @Prop({ default: "" }) readonly contextLabel!: string;
  @Prop({ default: "" }) readonly summary!: string;
  @Prop({ default: (): GenerateReportOption[] => [] }) readonly reportTypeOptions!: GenerateReportOption[];
  @Prop({ default: "" }) readonly selectedReportType!: string;
  @Prop({ default: (): GenerateReportOption[] => [] }) readonly countryOptions!: GenerateReportOption[];
  @Prop({ default: "" }) readonly selectedCountry!: string;
  @Prop({ default: false }) readonly showCountrySelector!: boolean;
  @Prop({ default: false }) readonly showCategorySelector!: boolean;
  @Prop({ default: "all" }) readonly categoryScopeMode!: ReportCategoryScopeMode;
  @Prop({ default: (): GenerateReportCategoryOption[] => [] }) readonly categoryOptions!: GenerateReportCategoryOption[];
  @Prop({ default: (): number[] => [] }) readonly selectedCategoryIds!: number[];
  @Prop({ default: false }) readonly isSubmitting!: boolean;
  @Prop({ default: false }) readonly isSubmitDisabled!: boolean;
  @Prop({ default: "" }) readonly submitLabel!: string;
  @Prop({ default: "" }) readonly loadingLabel!: string;
  @Prop({ default: "fa-download" }) readonly submitIconClass!: string;
  @Prop({ default: "" }) readonly countSummary!: string;
  @Prop({ default: false }) readonly showAllergenOption!: boolean;
  @Prop({ default: false }) readonly includeAllergens!: boolean;
  @Prop({ default: "" }) readonly allergenOptionLabel!: string;

  get translations() {
    return this.localizationState?.translations;
  }

  get resolvedModalTitle() {
    return this.modalTitle || this.translations.get("reports_generate_report_title");
  }

  get resolvedReportTypeLabel() {
    return this.translations.get("reports_report_type_label");
  }

  get resolvedCountryLabel() {
    return this.translations.get("reports_country_label");
  }

  get resolvedCategoriesLabel() {
    return this.translations.get("reports_categories_label");
  }

  get resolvedAllCategoriesLabel() {
    return this.translations.get("reports_all_categories");
  }

  get resolvedSelectedCategoriesLabel() {
    return this.translations.get("reports_selected_categories");
  }

  get resolvedAllCategoriesNote() {
    return this.translations.get("reports_all_categories_note");
  }

  get resolvedSubmitLabel() {
    return this.submitLabel || this.translations.get("reports_generate_report_title");
  }

  get resolvedLoadingLabel() {
    return this.loadingLabel || this.translations.get("reports_generating");
  }

  get resolvedCloseLabel() {
    return this.translations.get("close");
  }

  get resolvedAllergenOptionLabel() {
    return this.allergenOptionLabel || "Include allergens";
  }

  get isDishReportContext() {
    return this.reportContext === "dish";
  }

  get selectedReportTypeOption() {
    return this.reportTypeOptions.find((option) => option.value === this.selectedReportType) || this.reportTypeOptions[0];
  }

  get selectedCountryOption() {
    return this.countryOptions.find((option) => option.value === this.selectedCountry) || this.countryOptions[0];
  }

  get selectedCategoryOptions() {
    return this.categoryOptions.filter((option) => this.selectedCategoryIds.includes(option.value));
  }

  handleClose() {
    if (this.isSubmitting) {
      return;
    }

    this.$emit("close");
  }

  handleReportTypeChange(value: string | GenerateReportOption) {
    this.$emit("update:reportType", typeof value === "object" ? value.value : value);
  }

  handleCountryChange(value: string | GenerateReportOption) {
    this.$emit("update:country", typeof value === "object" ? value.value : value);
  }

  handleCategoryScopeChange(mode: ReportCategoryScopeMode) {
    this.$emit("update:categoryScopeMode", mode);
  }

  handleCategoryChange(value: Array<number | GenerateReportCategoryOption> | number | GenerateReportCategoryOption) {
    const items = Array.isArray(value) ? value : [value];
    const categoryIds = items
      .map((item) => typeof item === "object" ? item.value : item)
      .filter((item): item is number => typeof item === "number");

    this.$emit("update:categoryIds", categoryIds);
  }

  handleIncludeAllergensChange(event: Event) {
    const checked = (event.target as HTMLInputElement)?.checked ?? false;
    this.$emit("update:includeAllergens", checked);
  }
}
